import { createAuthLoader } from "@shared-ui/loaders/auth-loader";
import { ENV } from "@/config/env";
import { apiGet } from "@shared-ui/utils/api";
import {
  API_PRESETS,
  API_COMPANIES,
  API_DICTIONARIES,
  API_REALTIME_CONFIGS,
  API_TENANT_USERS,
  API_ONP_PROMPT_SETTINGS,
  API_GLOBAL_DICTIONARY,
  API_NOTIFICATIONS_UNREAD_COUNT,
  AUTH_REFRESH,
} from "@/utils/const";
import type {
  TenantUsers,
  TenantUsersResponse,
  WhoamiMe,
} from "@shared-domain/auth-service/contract";
import type {
  PresetsResponse,
  CompaniesResponse,
  DictionariesResponse,
  RealtimeConfigsResponse,
  NotificationUnreadCountResponse,
  DDBPreset,
  DDBCompany,
  DDBDictionary,
  DDBRealtimeConfig,
} from "@shared-domain/unizo/contract";
import type { DDBONPPromptSetting, DDBGlobalDictionaryEntry } from "@shared-domain/admin/contract";
import type { ONPSettingsMap } from "@/lib/prompts/compose";

export interface RootLoaderData {
  /** 認証情報 */
  auth: WhoamiMe;
  /** プリセット一覧（Unizo 固有） */
  presets: DDBPreset[];
  /** 会社マスター（Unizo 固有） */
  companies: DDBCompany[];
  /** ユーザーマスタ */
  users: TenantUsers[];
  /** 辞書マスタ（有効なもののみ） */
  dictionaries: DDBDictionary[];
  /** リアルタイムAPI設定（アクティブなもののみ） */
  realtimeConfigs: DDBRealtimeConfig[];
  /** ONPプロンプト設定（Admin管理、フォールバック用にundefined許容） */
  onpSettings?: ONPSettingsMap;
  /** グローバル辞書（Admin管理、テナント横断） */
  globalDictionary: DDBGlobalDictionaryEntry[];
  /** 通知未読数 */
  notificationUnreadCount: number;
}

/**
 * Root loader
 * 認証情報とグローバル設定を取得
 */
export async function rootLoader({
  request,
}: {
  request: Request;
}): Promise<RootLoaderData> {
  const url = new URL(request.url);

  // エラーページ（403/404）の場合はAPI呼び出しをスキップ
  // これにより、IP制限などで403が返る場合の無限リダイレクトを防ぐ
  if (url.pathname.includes("/403") || url.pathname.includes("/404")) {
    return {
      auth: {
        user: {
          id: "",
          name: "",
          email: "",
        },
        memberships: [],
        currentTenantId: "",
        info: undefined,
      },
      presets: [],
      companies: [],
      users: [],
      dictionaries: [],
      realtimeConfigs: [],
      onpSettings: undefined,
      globalDictionary: [],
      notificationUnreadCount: 0,
    };
  }

  // Phase 1: 認証処理（共通ロジック）
  const authLoader = createAuthLoader({
    apiEndpoint: ENV.PRODUCT_API_ENDPOINT,
    authPortalUrl: ENV.AUTH_PORTAL_URL,
    productId: ENV.PRODUCT_ID,
    tokenStoreName: "unizo",
    userPoolId: ENV.COGNITO_USERPOOL_ID,
    clientId: ENV.CLIENT_ID,
    authRefreshUrl: AUTH_REFRESH,
  });

  const auth = await authLoader({ request });

  // Phase 2: グローバル設定取得（Unizo 固有）
  // TODO: 認証成功後に tenantId を取得できるようにする
  // 現在は暫定で最初の membership の tenantId を使用
  const tenantId = auth.currentTenantId || auth.memberships[0]?.tenantId;

  // 並列取得 (404を許容するためallでなくallSettledを使用する)
  const [presetsResponse, companiesResponse, usersResponse, dictionariesResponse, realtimeConfigsResponse, onpSettingsResponse, globalDictionaryResponse, notificationUnreadCountResponse] =
    await Promise.allSettled([
      fetchPresets(tenantId),
      fetchCompanies(tenantId),
      fetchUsers(tenantId),
      fetchDictionaries(tenantId),
      fetchRealtimeConfigs(tenantId),
      fetchONPSettings(tenantId),
      fetchGlobalDictionary(tenantId),
      fetchNotificationUnreadCount(tenantId),
    ]);

  return {
    auth,
    presets:
      presetsResponse.status === "fulfilled" ? presetsResponse.value : [],
    companies:
      companiesResponse.status === "fulfilled" ? companiesResponse.value : [],
    users: usersResponse.status === "fulfilled" ? usersResponse.value : [],
    dictionaries:
      dictionariesResponse.status === "fulfilled" ? dictionariesResponse.value : [],
    realtimeConfigs:
      realtimeConfigsResponse.status === "fulfilled" ? realtimeConfigsResponse.value : [],
    onpSettings:
      onpSettingsResponse.status === "fulfilled" ? onpSettingsResponse.value : undefined,
    globalDictionary:
      globalDictionaryResponse.status === "fulfilled" ? globalDictionaryResponse.value : [],
    notificationUnreadCount:
      notificationUnreadCountResponse.status === "fulfilled" ? notificationUnreadCountResponse.value : 0,
  };
}

/**
 * プリセット一覧を取得
 */
async function fetchPresets(tenantId: string): Promise<DDBPreset[]> {
  const params = new URLSearchParams({ tenantId });
  const response = await apiGet<PresetsResponse>(
    `${API_PRESETS}?${params.toString()}`,
  );
  return response.success ? response.presets : [];
}

/**
 * 会社マスター一覧を取得
 */
async function fetchCompanies(tenantId: string): Promise<DDBCompany[]> {
  const params = new URLSearchParams({ tenantId, active: "true" });
  const response = await apiGet<CompaniesResponse>(
    `${API_COMPANIES}?${params.toString()}`,
  );
  return response.success ? response.companies : [];
}

/**
 * 辞書マスター一覧を取得（有効なもののみ）
 */
async function fetchDictionaries(tenantId: string): Promise<DDBDictionary[]> {
  const params = new URLSearchParams({ tenantId });
  const response = await apiGet<DictionariesResponse>(
    `${API_DICTIONARIES}?${params.toString()}`,
  );
  return response.success ? response.dictionaries : [];
}

/**
 * リアルタイムAPI設定一覧を取得（アクティブなもののみ）
 */
async function fetchRealtimeConfigs(tenantId: string): Promise<DDBRealtimeConfig[]> {
  const params = new URLSearchParams({ tenantId });
  const response = await apiGet<RealtimeConfigsResponse>(
    `${API_REALTIME_CONFIGS}?${params.toString()}`,
  );
  return response.success ? response.realtimeConfigs : [];
}

/**
 * ユーザーマスター一覧を取得
 */
async function fetchUsers(tenantId: string): Promise<TenantUsers[]> {
  const params = new URLSearchParams({ tenantId, active: "true" });
  const response = await apiGet<TenantUsersResponse>(
    `${API_TENANT_USERS}?${params.toString()}`,
  );
  return response.success ? response.users : [];
}

/**
 * ONPプロンプト設定を取得（Admin管理テーブル）
 * AdminServiceStack-{env}-ONPPromptSettings テーブルから LAYER1/LAYER2 等を取得
 * 取得失敗時はundefinedを返し、ハードコードフォールバックに委譲
 */
async function fetchONPSettings(tenantId: string): Promise<ONPSettingsMap | undefined> {
  try {
    const params = new URLSearchParams({ tenantId });
    const response = await apiGet<{ settings: DDBONPPromptSetting[] }>(
      `${API_ONP_PROMPT_SETTINGS}?${params.toString()}`,
    );
    if (!response.settings || response.settings.length === 0) return undefined;
    const map: ONPSettingsMap = {};
    for (const s of response.settings) {
      map[s.settingId] = s;
    }
    return map;
  } catch {
    return undefined;
  }
}

/**
 * グローバル辞書を取得（Admin管理テーブル）
 * 取得失敗時は空配列を返す
 */
async function fetchGlobalDictionary(tenantId: string): Promise<DDBGlobalDictionaryEntry[]> {
  try {
    const params = new URLSearchParams({ tenantId });
    const response = await apiGet<{ entries: DDBGlobalDictionaryEntry[] }>(
      `${API_GLOBAL_DICTIONARY}?${params.toString()}`,
    );
    return response.entries || [];
  } catch {
    return [];
  }
}

/**
 * 通知未読数を取得
 * 取得失敗時は0を返す
 */
async function fetchNotificationUnreadCount(tenantId: string): Promise<number> {
  try {
    const params = new URLSearchParams({ tenantId });
    const response = await apiGet<NotificationUnreadCountResponse>(
      `${API_NOTIFICATIONS_UNREAD_COUNT}?${params.toString()}`,
    );
    return response.success ? response.unreadCount : 0;
  } catch {
    return 0;
  }
}
